import React, { useState } from "react";
import { Play, CaretDown, CaretUp } from "@phosphor-icons/react";

// "What's coming on" strip underneath the active player — modeled on
// YouTube's mobile "Up next" list: a horizontal row of clean thumbnails
// with the TITLE printed below each one. No green play circle, no
// numbering overlay — just the thumbnail + title + speaker/artist,
// because the whole card is tappable.
//
// Default-OPEN now so users always see what's coming next without
// having to tap to expand. They can still collapse if it eats too much
// space.
//
// Props:
//   items     – full queue array (post-filter, post-search)
//   currentId – id of the currently-playing track in `items`
//   onSelect  – (item) => void
//   accent    – tailwind color name (terracotta, forest, purple, ...)
//   max       – max number of upcoming items to display (default 6)
//   defaultOpen – default true (YouTube-style)
//   progressMap – optional { [item.id]: { seconds, duration_sec } } —
//                 when present, paints a green progress bar across the
//                 bottom of each matching thumbnail and replaces the
//                 duration chip with a "Continue" badge. Powers the
//                 Continue Watching UX on the Movies player.
export default function UpNextStrip({ items, currentId, onSelect, accent = "terracotta", max = 6, defaultOpen = true, progressMap = null, orientation = "horizontal" }) {
  const [open, setOpen] = useState(defaultOpen);
  const vertical = orientation === "vertical";

  if (!Array.isArray(items) || items.length < 2) return null;
  const idx = items.findIndex((t) => t.id === currentId);
  if (idx < 0 || idx >= items.length - 1) return null;
  const next = items.slice(idx + 1, idx + 1 + max);
  if (next.length === 0) return null;

  const remaining = items.length - idx - 1;

  return (
    <div className={vertical ? "mt-4 -mx-1 lg:mt-2 lg:mx-0" : "mt-4 -mx-1"} data-testid="up-next-strip">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="up-next-toggle"
        aria-expanded={open}
        className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-cream/5 transition-colors"
      >
        <span className="text-[11px] tracking-[0.22em] uppercase text-cream/85 font-bold inline-flex items-center gap-1.5">
          <Play size={11} weight="fill" className={`text-${accent}`} /> Up next
          <span className={`ml-1 px-1.5 py-0.5 rounded-full bg-${accent}/20 text-${accent} text-[9px] tracking-wider`}>
            {remaining}
          </span>
        </span>
        <span className="text-[10px] text-cream/55 inline-flex items-center gap-1">
          {open ? "Hide" : "Show"}
          {open ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />}
        </span>
      </button>
      {open && (
        <div className={vertical ? "flex gap-3 overflow-x-auto pb-1 px-1 pt-2 snap-x lg:flex-col lg:gap-1 lg:overflow-x-visible lg:snap-none lg:px-1" : "flex gap-3 overflow-x-auto pb-1 px-1 pt-2 snap-x"} data-testid="up-next-list">
          {next.map((t) => {
            const subtitle = t.artist || t.speaker || t.teacher || t.story || "";
            const poster = t.youtube_id ? `https://i.ytimg.com/vi/${t.youtube_id}/mqdefault.jpg` : null;
            // Deterministic real-face fallback when the YouTube thumb
            // 404s or returns the 120×90 unavailable placeholder.
            // Matches the pattern used in MediaThumb / VideoCard so
            // every empty card on the page renders a consistent
            // photo-style fallback instead of a black tile.
            const seed = Math.abs([...((t.title || "") + (subtitle || "Holy Words"))].reduce((a, c) => a + c.charCodeAt(0), 0));
            const faceIdx = (seed % 70) + 1;
            const fallbackFace = `https://i.pravatar.cc/320?img=${faceIdx}`;
            const prog = progressMap && progressMap[t.id];
            const pct = prog && prog.duration_sec > 0
              ? Math.max(2, Math.min(100, (prog.seconds / prog.duration_sec) * 100))
              : 0;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onSelect && onSelect(t)}
                data-testid={`up-next-item-${t.id}`}
                className={`group snap-start text-left rounded-xl overflow-hidden bg-transparent hover:bg-cream/5 transition-colors ${vertical ? "shrink-0 w-44 lg:w-full lg:shrink lg:flex lg:gap-3 lg:items-center lg:p-2 lg:rounded-xl" : "shrink-0 w-44"}`}
              >
                <div className={`aspect-video relative bg-forest/60 overflow-hidden rounded-lg ${vertical ? "lg:w-28 lg:shrink-0" : ""}`}>
                  {poster ? (
                    <img
                      src={poster}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => {
                        if (!e.currentTarget.dataset.swapped) {
                          e.currentTarget.dataset.swapped = "1";
                          e.currentTarget.src = fallbackFace;
                        }
                      }}
                      onLoad={(e) => {
                        // 120×90 = YouTube's grey "video unavailable"
                        // placeholder. Swap to the face fallback so
                        // the card never looks broken.
                        if (e.currentTarget.naturalWidth <= 120 && !e.currentTarget.dataset.swapped) {
                          e.currentTarget.dataset.swapped = "1";
                          e.currentTarget.src = fallbackFace;
                        }
                      }}
                    />
                  ) : (
                    <img
                      src={fallbackFace}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                  {/* "Continue" badge replaces the duration chip when
                      the viewer has watched some of this title. Same
                      corner so existing layout stays intact. */}
                  {prog ? (
                    <div
                      className="absolute bottom-1.5 right-1.5 text-[9px] font-bold text-white bg-emerald-600/90 px-1.5 py-0.5 rounded uppercase tracking-wider"
                      data-testid={`up-next-continue-${t.id}`}
                    >
                      Continue
                    </div>
                  ) : t.duration ? (
                    <div className="absolute bottom-1.5 right-1.5 text-[9px] font-mono text-cream bg-ink/85 px-1.5 py-0.5 rounded">
                      {t.duration}
                    </div>
                  ) : null}
                  {/* Green progress bar pinned to the bottom edge —
                      YouTube-style. Width is computed from
                      seconds / duration_sec. */}
                  {pct > 0 && (
                    <div className="absolute left-0 right-0 bottom-0 h-1 bg-black/40">
                      <div
                        className="h-full bg-emerald-500"
                        style={{ width: `${pct}%` }}
                        data-testid={`up-next-progress-${t.id}`}
                      />
                    </div>
                  )}
                </div>
                <div className={`pt-2 pb-1 px-0.5 ${vertical ? "lg:flex-1 lg:min-w-0 lg:pt-0 lg:pb-0" : ""}`}>
                  <div className="text-[12px] font-semibold text-cream leading-snug line-clamp-2">{t.title}</div>
                  {subtitle && (
                    <div className="text-[10px] text-cream/60 truncate mt-0.5">{subtitle}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
